import {
  ArticleSavingContext,
  isBodyEmpty,
} from 'articleEditor/contexts/savingContext';
import { useBaseTranslation } from 'articleEditor/hooks/translations/useBaseTranslation';
import { useDefaultTranslation } from 'articleEditor/hooks/useDefaultTranslation';
import { RetranslationWarningModal } from 'articleEditor/modals/retranslationWarningModal';
import { useMyEditorRef } from 'lib-knowledge-document-structure/plateHooks';
import { LanguageContext } from 'lib-knowledge-management-common/contexts/language';
import { useModal } from 'lib-knowledge-modal';
import { TranslatingType } from 'lib-knowledge-remote-types/general.types';
import { useContext, useMemo } from 'react';
import { snapshot } from 'valtio';

export const useTranslateArticle = () => {
  const editor = useMyEditorRef();
  const { showModal } = useModal();
  const { currentLanguage: currentLanguageId } = useContext(LanguageContext);
  const { savingStore } = useContext(ArticleSavingContext);
  const {
    currentArticleDetails: { body: currentChildren, title: currentTitle },
  } = snapshot(savingStore);
  const defaultTranslation = useDefaultTranslation();
  const shouldShowRetranslationWarningModal = useMemo(() => {
    return !isBodyEmpty(currentChildren) || currentTitle !== 'Untitled';
  }, [currentChildren, currentTitle]);
  const {
    translate,
    stopLoadingAndShowErrorNotification,
    setGlobalLoading,
    currentLanguage,
    clearChildren,
    clearTitle,
  } = useBaseTranslation({
    translatingType: TranslatingType.ARTICLE,
    onParsedBlock: (block, setChildren) => {
      setChildren([...editor.children, block]);
    },
  });

  const translateArticle = async () => {
    if (!defaultTranslation?.draft.content?.children) {
      stopLoadingAndShowErrorNotification();
      return;
    }
    try {
      setGlobalLoading(true);
      await clearTitle();
      clearChildren();
      await translate({
        title: defaultTranslation.draft.title,
        content: defaultTranslation.draft.content.children,
      });
    } catch {
      stopLoadingAndShowErrorNotification();
    }
  };

  return async (): Promise<void> => {
    if (shouldShowRetranslationWarningModal) {
      showModal(RetranslationWarningModal, {
        title: 'Retranslate article?',
        body: `Are you sure you want to retranslate this article in '${currentLanguage?.description ?? currentLanguageId}'. This action will overwrite the existing translation and it is NOT reversible.`,
        onAccept: () => translateArticle(),
      });
    } else {
      await translateArticle();
    }
  };
};
